import React, { useState } from "react";

import {
  Typography,
  Modal,
  TextField,
  InfoPanel,
  ButtonLink,
  Checkbox,
} from "@king-ict/eupisi-ui/components";
import {ReactComponent as DangerPanel} from "@king-ict/eupisi-ui/components/Icon/InfoDangerSmall.svg"; 
import { useInput, useValidation } from "@king-ict/eupisi-ui/hooks";
import { nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { canCreateRequest, getPersonCheckData } from "../../../api";
import { newRequestUrl, newPreschoolRequestUrl, publicWebAplicationUrl } from "../../../app";
import {
  CreateRequest,
  CreateRequestInitial,
  CreateRequestValidationScheme,
  ParentAndChildDataCheckStatusEnum,
  RequestTypeEnum,
} from "../../../models";
import { CanCreateRequestStatusEnum } from "../../../models/Request/CanCreateRequestStatusEnum";
import styles from "./CreateRequestModal.module.scss";

interface Props {
  showCreateRequestModal: boolean;
  closeModal: () => void;
  requestTypeId?: RequestTypeEnum;
}

export function CreateRequestModal({
  showCreateRequestModal,
  closeModal,
  requestTypeId,
}: Props) {
  const [
    createRequestData,
    setCreateRequestData,
  ] = React.useState<CreateRequest>(CreateRequestInitial);
  const { t } = useTranslation();
  const { handleChange } = useInput();

  const [errors, doValidations, stopValidations] = useValidation({
    data: createRequestData,
    validationSchema: CreateRequestValidationScheme,
  });

  const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false);
  
  const handleToggleCancelModal = () => {
    setIsConfirmModalOpened((prev) => !prev);
  };

  const handleConfirmActionModal = () => {  
    handleCreateRequest(createRequestData.applicantParentIntegrationResultStatus, createRequestData.childIntegrationResultStatus);
  };

  const handleConfirmModal = () => {
    const isValid = doValidations();
    if (isValid) {
      getPersonCheckData(
        createRequestData.applicantParentFirstName,
        createRequestData.applicantParentLastName,
        createRequestData.applicantParentOib,
        createRequestData.childFirstName,
        createRequestData.childLastName,
        createRequestData.childOib
      ).then((res) => {
        setCreateRequestData((prev) => ({
          ...prev,
          applicantParentIntegrationResultStatus: res.applicantStatus,
          childIntegrationResultStatus: res.childStatus,
          canCreateRequestStatus: CanCreateRequestStatusEnum.Unknown,
        }));
        if (res.applicantStatus === ParentAndChildDataCheckStatusEnum.Success) {

          if (res.childStatus == ParentAndChildDataCheckStatusEnum.Success) {                
            handleCreateRequest(res.applicantStatus, res.childStatus);
          }
          if (res.childStatus == ParentAndChildDataCheckStatusEnum.NoCustody) {           
            setIsConfirmModalOpened((prev) => !prev);
            return false;
          }  
        }        
      });
    }
  };

  const handleCloseModal = () => {
    closeModal();
    setCreateRequestData({} as CreateRequest);
    stopValidations();
  };

  const handleGetRequestExistMessage = () => {
    switch (createRequestData.canCreateRequestStatus) {
      case CanCreateRequestStatusEnum.RequestExist:
        return (
          <InfoPanel
            message={t("requestList.createRequestExist")}
            color="warning"
          />
        );
      case CanCreateRequestStatusEnum.MaximumRequestCount:
        return (
          <InfoPanel
            message={t("requestList.maximumRequestCount")}
            color="warning"
          />
        );
      default:
        return <></>;
    }
  };

  const handleGetApplicantMessage = () => {
    switch (createRequestData.applicantParentIntegrationResultStatus) {
      case ParentAndChildDataCheckStatusEnum.DataMismatch:
        return (
          <InfoPanel
            message={t("requestList.applicantParentMismatchMessage")}
            color="danger"
          />
        );
      case ParentAndChildDataCheckStatusEnum.OibFailed:
        return (
          <InfoPanel
            message={t("requestList.applicantParentOibFailedMessage")}
            color="warning"
          />
        );
        case ParentAndChildDataCheckStatusEnum.PersonInformationDoesNotExist:
        return (
          <InfoPanel
            message={t("requestList.applicantInformationDoesNotExist")}
            color="danger"
          />
        );
      default:
        return <></>;
    }
  };

  const handleGetChildMessage = () => {
    switch (createRequestData.childIntegrationResultStatus) {
      case ParentAndChildDataCheckStatusEnum.DataMismatch:
        return (
          <InfoPanel
            message={t("requestList.childMismatchMessage")}
            color="danger"
          />
        );
      case ParentAndChildDataCheckStatusEnum.OibFailed:
        return (
          <InfoPanel
            message={t("requestList.childOibFailedMessage")}
            color="warning"
          />
        ); 
        case ParentAndChildDataCheckStatusEnum.PersonInformationDoesNotExist:
        return (
          <InfoPanel
            message={t("requestList.childInformationDoesNotExist")}
            color="danger"
          />
        );
      default:
        return <></>;
    }
  };

  const handleCreateRequest = (applicantStatus: ParentAndChildDataCheckStatusEnum, childStatus: ParentAndChildDataCheckStatusEnum) => {
    canCreateRequest(
      createRequestData.childOib,
      requestTypeId ? requestTypeId : RequestTypeEnum.Kindergarten
    ).then((requestRes) => {
      if (
        requestRes.canCreateRequestStatus ===
        CanCreateRequestStatusEnum.Success
      ) {
        stopValidations();
        window.location.href =
          requestTypeId === RequestTypeEnum.Preschool
            ? newPreschoolRequestUrl(
                createRequestData.applicantParentOib,
                createRequestData.childOib,
                childStatus === ParentAndChildDataCheckStatusEnum.Success
              )
            : newRequestUrl(
                createRequestData.applicantParentOib,
                createRequestData.childOib,
                childStatus === ParentAndChildDataCheckStatusEnum.Success
              );
      } else {
        setIsConfirmModalOpened(false);
        setCreateRequestData((prev) => ({
          ...prev,
          applicantParentIntegrationResultStatus: applicantStatus,
          childIntegrationResultStatus: childStatus,
          canCreateRequestStatus: requestRes.canCreateRequestStatus,
        }));
      }
    });
  }

  const handleGoToEUpisiPublic = () => {
    window.open(
      (`${publicWebAplicationUrl}`), "_blank"
    )  
  };

  return (
    <><Modal
      title={t("requestList.createNewRequest")}
      open={showCreateRequestModal}
      onClose={handleCloseModal}
      onConfirm={handleConfirmModal}
      showCloseMark={false}
      type="confirm"
      cancelText={t("common.quit")}
      confirmText={t("requestList.createNewRequest")}
    >
      <Row>
        <Col md={12}>{handleGetRequestExistMessage()}</Col>
      </Row>
      <Row>
        <Col md={12}>
          <Typography variant="mediumBold" component="h2">
            {t("requestList.enterParentInformation")}
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col md={12} className={`${styles.modalSubtitles}`}>
          {handleGetApplicantMessage()}
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label={t("requestList.applicantParentOib")}
            value={createRequestData.applicantParentOib || ""}
            name={nameof<typeof createRequestData>("applicantParentOib")}
            onChange={(e) => handleChange(e, setCreateRequestData)}
            errorText={errors?.applicantParentOib}
            maxLength={11} />
        </Col>
        <Col md={6}>
          <TextField
            label={t("requestList.applicantParentFirstName")}
            value={createRequestData.applicantParentFirstName || ""}
            name={nameof<typeof createRequestData>("applicantParentFirstName")}
            onChange={(e) => handleChange(e, setCreateRequestData)}
            errorText={errors?.applicantParentFirstName} />
        </Col>
      </Row>
      <Row className={`${styles.modalLastRowInSection}`}>
        <Col md={9}>
          <TextField
            label={t("requestList.applicantParentLastName")}
            value={createRequestData.applicantParentLastName || ""}
            name={nameof<typeof createRequestData>("applicantParentLastName")}
            onChange={(e) => handleChange(e, setCreateRequestData)}
            errorText={errors?.applicantParentLastName} />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Typography variant="mediumBold" component="h2">
            {t("requestList.enterChildInformation")}
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col md={12} className={`${styles.modalSubtitles}`}>
          {handleGetChildMessage()}
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <TextField
            label={t("requestList.childOib")}
            value={createRequestData.childOib || ""}
            name={nameof<typeof createRequestData>("childOib")}
            onChange={(e) => handleChange(e, setCreateRequestData)}
            errorText={errors?.childOib}
            maxLength={11} />
        </Col>
        <Col md={6}>
          <TextField
            label={t("requestList.childFirstName")}
            value={createRequestData.childFirstName || ""}
            name={nameof<typeof createRequestData>("childFirstName")}
            onChange={(e) => handleChange(e, setCreateRequestData)}
            errorText={errors?.childFirstName} />
        </Col>
      </Row>
      <Row>
        <Col md={9}>
          <TextField
            label={t("requestList.childLastName")}
            value={createRequestData.childLastName || ""}
            name={nameof<typeof createRequestData>("childLastName")}
            onChange={(e) => handleChange(e, setCreateRequestData)}
            errorText={errors?.childLastName} />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ButtonLink onClick={() => handleGoToEUpisiPublic()}>
            {t("requestList.publicUserApp")}
          </ButtonLink>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Checkbox
            label={t("requestList.confirmationPublicLogIn")}
            name={nameof<typeof createRequestData>("isPublicLogInComfirmd")}
            value={createRequestData?.isPublicLogInComfirmd}            
            onChange={(e) => handleChange(e, setCreateRequestData)}
            errorText={errors?.isPublicLogInComfirmd}
          />      
          </Col>
      </Row>
    </Modal>
    
    
    <Modal
      type={"confirm"}
      title={t("requestList.confirmParentChildNoCustodyMessage")}
      cancelText={t("common.no")}
      confirmText={t("common.yes")}
      open={isConfirmModalOpened}
      onClose={handleToggleCancelModal}
      onConfirm={handleConfirmActionModal}
    >
        {t("requestList.parentChildNoCustodyMessage")}
      </Modal></>
  );
}
